import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, Database, ShieldCheck, UserCheck, AlertCircle } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-slate-50 pt-32 pb-20">
      {/* Futuristic grid background */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, #1e293b 1px, transparent 1px),
            linear-gradient(to bottom, #1e293b 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-violet-600 to-purple-600 rounded-2xl mb-6 shadow-xl shadow-violet-500/30"
          >
            <Lock className="w-10 h-10 text-white" />
          </motion.div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-4">
            Privacy Policy
          </h1>
          
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="h-1 bg-gradient-to-r from-violet-600 to-purple-600 mx-auto rounded-full mb-4"
          />
          
          <p className="text-slate-600 text-lg">Last updated: March 25, 2026</p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 p-8 md:p-12"
        >
          <div className="prose prose-slate max-w-none">
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <Eye className="w-7 h-7 mr-3 text-violet-600" />
                Introduction
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                TransactWorld Limited ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our payment gateway services and visit our website. By using our services, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <Database className="w-7 h-7 mr-3 text-violet-600" />
                Information We Collect
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                We collect various types of information to provide and improve our services:
              </p>
              
              <div className="space-y-4">
                <div className="p-5 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-100">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Personal Information</h3>
                  <p className="text-slate-700 leading-relaxed text-justify">
                    Name, email address, phone number, business information, billing address, and payment card details. This information is necessary to create and manage your account, process transactions, and comply with regulatory requirements.
                  </p>
                </div>
                
                <div className="p-5 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-100">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Transaction Information</h3>
                  <p className="text-slate-700 leading-relaxed text-justify">
                    Details about payments you send or receive, including transaction amounts, dates, merchant information, and payment methods. We retain this information for record-keeping and regulatory compliance.
                  </p>
                </div>
                
                <div className="p-5 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-100">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Technical Information</h3>
                  <p className="text-slate-700 leading-relaxed text-justify">
                    IP address, browser type, device information, operating system, and usage data. This helps us optimize our platform and detect fraudulent activities.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <UserCheck className="w-7 h-7 mr-3 text-violet-600" />
                How We Use Your Information
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-none space-y-3">
                {[
                  'Process and manage payment transactions',
                  'Verify your identity and prevent fraud',
                  'Comply with legal and regulatory requirements',
                  'Improve and optimize our services',
                  'Provide customer support',
                  'Send important updates and notifications',
                  'Conduct risk assessment and monitoring',
                  'Develop new features and services'
                ].map((item, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + idx * 0.05 }}
                    className="flex items-start space-x-3 p-3 bg-gradient-to-r from-violet-50/50 to-purple-50/50 rounded-lg"
                  >
                    <div className="w-2 h-2 bg-violet-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <ShieldCheck className="w-7 h-7 mr-3 text-violet-600" />
                Data Security
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. Our security measures include:
              </p>
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-100 p-6">
                <ul className="space-y-2 text-slate-700">
                  <li>• PCI DSS Level 1 compliance for payment card data</li>
                  <li>• SSL/TLS encryption for data transmission</li>
                  <li>• Regular security audits and penetration testing</li>
                  <li>• Multi-factor authentication for account access</li>
                  <li>• Encrypted data storage and backups</li>
                  <li>• 24/7 security monitoring and incident response</li>
                </ul>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Data Sharing and Disclosure</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                We do not sell your personal information to third parties. However, we may share your information with:
              </p>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                <strong>Service Providers:</strong> Third-party companies that help us operate our business, such as payment processors, cloud hosting providers, and analytics services. These providers are contractually obligated to protect your information.
              </p>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                <strong>Financial Institutions:</strong> Banks and payment networks involved in processing your transactions.
              </p>
              <p className="text-slate-700 leading-relaxed text-justify">
                <strong>Legal Authorities:</strong> When required by law, regulation, legal process, or to protect our rights and the safety of our users.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Your Rights</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                Under applicable data protection laws, you have the right to:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Access your personal data',
                  'Correct inaccurate data',
                  'Request data deletion',
                  'Object to data processing',
                  'Data portability',
                  'Withdraw consent'
                ].map((right, idx) => (
                  <div key={idx} className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-100">
                    <span className="text-slate-700 font-medium">{right}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Data Retention</h2>
              <p className="text-slate-700 leading-relaxed text-justify">
                We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Transaction data is typically retained for a minimum of 7 years as required by financial regulations.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <AlertCircle className="w-7 h-7 mr-3 text-violet-600" />
                Changes to This Policy
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last Updated" date. Your continued use of our services after such modifications constitutes your acknowledgment and acceptance of the modified policy.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Contact Us</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact our Data Protection Officer:
              </p>
              <div className="p-6 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-100">
                <p className="text-slate-800 font-semibold mb-2">Data Protection Officer</p>
                <p className="text-slate-700">TransactWorld Limited</p>
                <p className="text-slate-700">Email: dpo@transactworld.com</p>
                <p className="text-slate-700">Phone: +44 20 3129 7109</p>
                <p className="text-slate-700 mt-2">Address: 3 Thivon Street, Anna Court 8-9, Office 202, CY-1056 Nicosia, Cyprus</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;